/**
 * GA4 event tracking utility for PulseSignal.
 *
 * Wraps window.gtag() with typed helper functions for every
 * trackable user action. Import and call from any component.
 *
 * Events are sent to GA4 (G-0H2D8NMSFJ) and also available
 * in the admin dashboard via the GA4 Data API server-side pull.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
  }
}

export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params)
  }
}

/**
 * Funnel page-view event for GA4. Fired by <PageBeacon /> on each real
 * navigation so the top-of-funnel (visited /pricing, started /signup) shows
 * up in GA4 alongside the backend's own anonymous beacon. utm_source is
 * passed through when present so paid acquisition is attributable. Guarded by
 * trackEvent for the gtag-undefined case (script not loaded / consent not
 * granted), so this is a safe no-op until GA4 is live.
 */
export const pageViewRecorded = (path: string, utm_source?: string) => {
  trackEvent('page_view', utm_source ? { path, utm_source } : { path })
}

export const analytics = {
  // Pricing page
  pricingPageViewed: () => trackEvent('pricing_page_viewed'),
  planSelected: (plan: string, interval: string) => trackEvent('plan_selected', { plan, interval }),
  checkoutStarted: (plan: string, price: number) => trackEvent('checkout_started', { plan, price }),
  checkoutCompleted: (plan: string, price: number) => trackEvent('checkout_completed', { plan, price }),

  // Auth
  signupStarted: () => trackEvent('signup_started'),
  signupCompleted: (method: string) => trackEvent('signup_completed', { method }),
  loginCompleted: () => trackEvent('login_completed'),

  // Companies
  companySearched: (query: string) => trackEvent('company_searched', { query: query.substring(0, 50) }),
  companyTracked: (name: string) => trackEvent('company_tracked', { company: name }),
  companyUntracked: (name: string) => trackEvent('company_untracked', { company: name }),
  companyViewed: (name: string) => trackEvent('company_viewed', { company: name }),

  // Dashboard
  dashboardViewed: () => trackEvent('dashboard_viewed'),
  moduleViewed: (module: string) => trackEvent('module_viewed', { module }),

  // Features
  compareUsed: (count: number) => trackEvent('compare_used', { company_count: count }),
  exportUsed: (format: string) => trackEvent('export_used', { format }),
  reportGenerated: () => trackEvent('report_generated'),
  battlecardViewed: (competitor: string) => trackEvent('battlecard_viewed', { competitor }),

  // Alerts
  alertConfigured: (type: string) => trackEvent('alert_configured', { alert_type: type }),

  // Signals
  signalFeedViewed: () => trackEvent('signal_feed_viewed'),
  signalClicked: (type: string) => trackEvent('signal_clicked', { signal_type: type }),

  // Upgrade
  upgradeBannerShown: (feature: string, plan: string) => trackEvent('upgrade_banner_shown', { feature, current_plan: plan }),
  upgradeClicked: (from: string, to: string, trigger: string) => trackEvent('upgrade_clicked', { from_plan: from, to_plan: to, trigger }),
}
